import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, Phone } from "lucide-react";

const WHATSAPP_NUMBER = "919270425109";

const plans = [
  {
    name: "Nurse Visit",
    subtitle: "Nursing Care Givers (Home Nursing / Patient Care)",
    theme: "pink",
    prices: [{ label: "Per Visit", amount: "650 - 1,100" }],
    startingFrom: true,
    features: [
      "Injection or Intravenous (IV) Medication",
      "Assisting With Catheters, Oxygen Equipment, or Feeding Tubes",
      "Home Assessment",
      "Wound Care",
      "Nebulization",
    ],
    popular: false,
  },
  {
    name: "Home Nursing Care",
    subtitle: "Home Nursing / Patient Care",
    theme: "blue",
    prices: [
      { label: "10 Hours", amount: "1,450" },
      { label: "24 Hours", amount: "1,650" },
    ],
    startingFrom: true,
    features: [
      "Nursing Caregiving",
      "Medicine Administration",
      "Mobility Support",
      "Vital Monitoring",
      "Urinary Catheterisation Care",
      "Palliative & Hospice Care At Home",
      "Wound Care",
      "Stroke Rehabilitation at Home",
    ],
    popular: true,
  },
  {
    name: "Critical Care Nursing",
    subtitle: "Advanced Critical Care",
    theme: "teal",
    prices: [
      { label: "10 Hours", amount: "1,850" },
      { label: "24 Hours", amount: "2,550" },
    ],
    startingFrom: false,
    features: [
      "Post Operative Care",
      "Patient Monitoring and Assessment",
      "Managing Life Support Equipment",
      "Emergency Response and Intervention",
      "Ensuring Patient Safety",
    ],
    popular: false,
  },
];

export default function HomeNursing() {
  return (
    <div className="pt-24 pb-20 bg-gradient-to-b from-[#EFF6FF] to-white min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#14B8A6] font-body mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-semibold text-blue-500 uppercase tracking-wider font-body">Services</span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight mt-3">
              Home Nursing
            </h1>
            <p className="text-base md:text-lg text-[#64748B] mt-4 font-body">
              Professional nursing care from trained medical professionals at your doorstep. 
              From wound care to critical monitoring, our nurses provide expert medical attention with care.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]"
          >
            <img 
              src="/images/home_nursing.png" 
              alt="Home Nursing" 
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.12, duration: 0.4 }}
              data-testid={`plan-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative rounded-3xl p-7 border flex flex-col ${
                plan.theme === "pink"
                  ? "border-brand-secondary/20 bg-gradient-to-b from-brand-secondary-light to-white shadow-xl shadow-brand-secondary/5"
                  : plan.theme === "teal"
                    ? "border-teal-200 bg-gradient-to-b from-teal-50 to-white shadow-xl shadow-teal-500/5"
                    : plan.theme === "blue" || plan.popular
                      ? "border-blue-200 bg-gradient-to-b from-blue-50 to-white shadow-xl shadow-blue-500/5"
                      : "border-slate-100 bg-white shadow-sm"
              }`}
            >
              {(plan.popular || plan.theme === "teal" || plan.theme === "pink") && (
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-white text-xs font-semibold font-body ${
                  plan.theme === "pink" ? "bg-brand-secondary" : plan.theme === "teal" ? "bg-teal-500" : "bg-blue-500"
                }`}>
                  {plan.popular ? "Most Chosen" : "Recommended"}
                </span>
              )}
              <h3 className="font-heading text-lg font-bold text-[#0F172A] mb-1">{plan.name}</h3>
              <p className="text-xs text-[#64748B] font-body mb-5">{plan.subtitle}</p>

              <div className="flex gap-3 mb-1">
                {plan.prices.map((p) => (
                  <div key={p.label} className="flex-1 bg-slate-50 rounded-xl p-3 text-center">
                    <p className="text-[10px] text-[#64748B] font-body mb-0.5">{p.label}</p>
                    <p className="font-heading text-lg font-bold text-[#0F172A]">&#8377;{p.amount}</p>
                  </div>
                ))}
              </div>
              {plan.startingFrom && (
                <p className="text-[10px] text-[#94A3B8] font-body mb-4 text-center">Starting from</p>
              )}
              {!plan.startingFrom && <div className="mb-4" />}

              <ul className="space-y-2.5 mb-6 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      plan.theme === "pink" ? "text-brand-secondary" : plan.theme === "teal" ? "text-teal-500" : "text-blue-500"
                    }`} strokeWidth={2} />
                    <span className="text-sm text-[#475569] font-body leading-snug">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%2C%20I%20am%20interested%20in%20${encodeURIComponent(plan.name)}%20Home%20Nursing`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`w-full inline-flex items-center justify-center gap-2 h-11 rounded-full font-semibold text-sm font-body transition-all ${
                  plan.theme === "pink"
                    ? "bg-brand-secondary text-white hover:bg-[#fb5d77] shadow-md shadow-brand-secondary/20"
                    : plan.theme === "teal"
                      ? "bg-teal-500 text-white hover:bg-teal-600 shadow-md shadow-teal-500/20"
                      : "bg-blue-500 text-white hover:bg-blue-600 shadow-md shadow-blue-500/20"
                }`}
              >
                Enquire Now
              </a>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-[#64748B] font-body italic mb-4">
            * Prices may vary depending on condition &amp; duration.
          </p>
          <a href="tel:+919270425109" className="inline-flex items-center gap-2 text-[#14B8A6] font-semibold font-body hover:underline">
            <Phone className="w-4 h-4" /> Call for custom requirements: +91 9270425109
          </a>
        </div>
      </div>
    </div>
  );
}
